/**
 * NavLateral — a lista de telas do sistema (`<nav aria-label="Menu principal">`).
 *
 * Recebe os itens JÁ FILTRADOS pelo papel (`itensVisiveis`) e desenha: itens de topo (Dashboard) soltos e
 * os grupos por tarefa. Regras do padrão A (item 3, 4 e 5):
 *  - acordeão EXCLUSIVO (um grupo aberto) só quando há mais de 16 itens visíveis; abaixo disso os grupos
 *    ficam sempre abertos (sem botão: nada para esconder);
 *  - o grupo da tela atual abre sozinho e o item ativo é rolado para dentro da área visível ao carregar
 *    (antes ficava escondido atrás de uma seção fechada ou abaixo da dobra);
 *  - item de 44 px (48 px no toque), ícone de 22 px, rótulo em frase; estado ativo = `aria-current="page"`
 *    em UM item só (o de maior prefixo, calculado em `registro.ts`), com barra à esquerda + peso 700 +
 *    fundo — o fundo sozinho dá 1,10:1 sobre a superfície, então a barra e o peso são obrigatórios
 *    (WCAG 1.4.1 e 1.4.11);
 *  - grupo fechado usa `hidden` (não sai do DOM: o `aria-controls` aponta para algo real) e NÃO entra na
 *    tabulação — a gaveta fechada do Sentinela deixava 13 itens na tabulação (NAV-03).
 *
 * Estilo: classes `.nav-*` e variáveis `--nav-*` do `index.css` (um par claro/escuro por papel; contraste
 * medido, ver o comentário lá). Nenhuma cor mora neste arquivo.
 */
import { ChevronDown, ExternalLink } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { LIMITE_ACORDEAO, organizarMenu, usaAcordeao } from "./registro.js";
import type { GrupoNav, ItemNav, RenderLinkShell } from "./tipos.js";
import { junta } from "./util.js";

export interface NavLateralProps {
  /** Itens que o papel pode abrir (já filtrados). */
  itens: readonly ItemNav[];
  grupos: readonly GrupoNav[];
  /** `ItemNav.id` da tela atual (`itemAtivo`), ou `null` em rota fora do registro. */
  ativoId: string | null;
  renderLink: RenderLinkShell;
  /** Chamado no clique de um item (a gaveta fecha). */
  aoNavegar?: () => void;
  limiteAcordeao?: number;
  rotuloDoMenu?: string;
  className?: string;
}

function Contador({ c }: { c: NonNullable<ItemNav["contador"]> }) {
  const texto = c.valor > 99 ? "99+" : String(c.valor);
  return (
    <span className={junta("nav-contador", c.tom === "atraso" && "nav-contador-atraso")}>
      <span aria-hidden="true">{texto}</span>
      <span className="sr-only">{c.rotulo}</span>
    </span>
  );
}

export function NavLateral({
  itens,
  grupos,
  ativoId,
  renderLink,
  aoNavegar,
  limiteAcordeao = LIMITE_ACORDEAO,
  rotuloDoMenu = "Menu principal",
  className,
}: NavLateralProps) {
  const base = useId();
  const navRef = useRef<HTMLElement>(null);
  const menu = organizarMenu(itens, grupos);
  const acordeao = usaAcordeao(itens.length, limiteAcordeao);
  const ativo = itens.find((i) => i.id === ativoId) ?? null;

  // Acordeão exclusivo: o grupo da tela atual é o aberto. Sem tela atual (rota fora do registro), o primeiro.
  const [aberto, setAberto] = useState<string | null>(() => ativo?.grupo ?? menu.grupos[0]?.grupo.id ?? null);
  useEffect(() => {
    if (ativo?.grupo) setAberto(ativo.grupo);
  }, [ativo?.grupo, ativoId]);

  // Rola o menu até o item ativo (só o menu: `scrollIntoView` arrastaria a página junto).
  useEffect(() => {
    const nav = navRef.current;
    const el = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !el || el.getClientRects().length === 0) return;
    const folga = 8;
    if (el.offsetTop < nav.scrollTop + folga) nav.scrollTop = Math.max(0, el.offsetTop - folga);
    else if (el.offsetTop + el.offsetHeight > nav.scrollTop + nav.clientHeight - folga)
      nav.scrollTop = el.offsetTop + el.offsetHeight - nav.clientHeight + folga;
  }, [ativoId, aberto]);

  const desenhaItem = (item: ItemNav) => {
    const Icone = item.icone;
    const eAtivo = item.id === ativoId;
    return (
      <li key={item.id}>
        {renderLink({
          // Com pílula, o clique abre a lista que o número conta (`contador.href`); sem ela, a tela de sempre.
          href: item.contador?.href ?? item.href,
          className: "nav-item",
          ariaCurrent: eAtivo ? "page" : undefined,
          aoClicar: aoNavegar,
          externo: item.externo,
          children: (
            <>
              <Icone size={22} strokeWidth={1.75} aria-hidden="true" className="nav-item-icone" />
              {/* `data-r` reserva a largura do rótulo em negrito: o peso 700 do item ativo não pode empurrar o texto. */}
              <span className="nav-rotulo" data-r={item.rotulo}>{item.rotulo}</span>
              {item.contador && <Contador c={item.contador} />}
              {item.externo && (
                <>
                  <ExternalLink size={14} aria-hidden="true" className="nav-item-icone" />
                  <span className="sr-only"> (abre em outra aba)</span>
                </>
              )}
            </>
          ),
        })}
      </li>
    );
  };

  return (
    <nav ref={navRef} aria-label={rotuloDoMenu} className={junta("nav-lateral", className)}>
      {menu.topo.length > 0 && <ul className="nav-lista">{menu.topo.map(desenhaItem)}</ul>}
      {menu.grupos.map(({ grupo, itens: doGrupo }) => {
        const idBotao = `${base}-b-${grupo.id}`;
        const idLista = `${base}-l-${grupo.id}`;
        const eAberto = !acordeao || aberto === grupo.id;
        // Grupo FECHADO com item que tem contador: um ponto no título avisa que há pendência lá dentro (SEN-30).
        // Sem isto a pílula de "Circuitos fora do ar" ficava escondida atrás do grupo fechado e o número não
        // servia para o que existe: avisar SEM abrir a tela. O ponto é vermelho só se algum contador é de atraso.
        const comContador = doGrupo.filter((i) => i.contador);
        const pendenciaOculta = acordeao && !eAberto && comContador.length > 0;
        const pendenciaUrgente = comContador.some((i) => i.contador?.tom === "atraso");
        return (
          <div key={grupo.id} className="nav-grupo-bloco">
            {acordeao ? (
              <button
                type="button"
                id={idBotao}
                className="nav-grupo"
                aria-expanded={eAberto}
                aria-controls={idLista}
                onClick={() => setAberto((a) => (a === grupo.id ? null : grupo.id))}
              >
                <span className="nav-grupo-rotulo">
                  {grupo.rotulo}
                  {pendenciaOculta && (
                    <>
                      <span className={junta("nav-grupo-ponto", pendenciaUrgente && "nav-grupo-ponto-atraso")} aria-hidden="true" />
                      <span className="sr-only"> (com pendências: {comContador.map((i) => i.rotulo).join(", ")})</span>
                    </>
                  )}
                </span>
                <ChevronDown size={14} aria-hidden="true" className={junta("nav-grupo-seta", !eAberto && "nav-grupo-seta-fechada")} />
              </button>
            ) : (
              <p id={idBotao} className="nav-grupo nav-grupo-fixo">{grupo.rotulo}</p>
            )}
            <ul id={idLista} className="nav-lista" hidden={!eAberto} aria-labelledby={idBotao}>
              {doGrupo.map(desenhaItem)}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}
